import { LitElement, html } from 'lit';
import iconStyles from './fil-icon.styles.js';

// get icon name according to file name
export const iconName = fileName =>
  fileName
    .replace(/\w*[^-]/g, m => m.charAt(0).toUpperCase() + m.substring(1).toLowerCase())
    .replace(/-/g, '');

/**
 * @tag fil-icon
 * @property {boolean} [filled=false] Toggle to a solid fill (available only for icons in the group Toggle and Logos)
 * @property {"blue-400"|"blue-500"|"blue-600"|"blue-800"|"blue-900"|"teal-400"|"teal-500"|"teal-600"|"teal-800"|"green-400"|"green-500"|"green-600"|"green-800"|"yellow-400"|"yellow-500"|"yellow-600"|"yellow-800"|"orange-400"|"orange-500"|"orange-600"|"orange-800"|"purple-400"|"purple-500"|"purple-600"|"purple-800"|"grey-050"|"grey-100"|"grey-200"| "grey-300"|"grey-400"|"grey-500"|"grey-600"|"grey-700"|"grey-800"|"grey-900"|"brand-black"|"brand-error-red"} [color] Colour from the GDS Palette. [Browse all GDS colours on design.fil.com](https://design.fil.com/brand-identity/colours/)
 * @property {string} [icon] Icon name. Browse all icons on https://design.fil.com/brand-identity/icons
 * @property {string} [label] Add a label according to how your icon is used
 * @property {"left"|"right"} [position] Alignment of the icon (this is for icons that don´t use the full square canvas)
 * @property {"6"|"8"|"12"|"16"|"20"|"24"|"32"|"36"|"48"|"64"} [size] Presets for fix icon sizes
 */
export class FilIcon extends LitElement {
  static properties = {
    color: { type: String },
    filled: { type: Boolean },
    icon: { type: String },
    label: { type: String },
    position: { type: String },
    size: { type: String },
    svg: { type: String },
  };

  static styles = [iconStyles];

  constructor() {
    super();
    this.svg = '';
  }

  svgModifier(svg) {
    if (!svg) {
      return;
    }
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', this.label ?? this.icon);
    if (this.label) {
      svg.setAttribute('aria-label', this.label);
      svg.setAttribute('title', this.label);
      svg.setAttribute('aria-hidden', 'false');
    } else {
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('title', this.icon);
    }

    if (this.size) {
      svg.setAttribute('data-size', this.size);
      svg.classList.remove('inherit-size');
    } else {
      svg.removeAttribute('data-size');
      svg.classList.add('inherit-size');
    }

    if (this.position) {
      svg.setAttribute('data-position', this.position);
    } else {
      svg.removeAttribute('data-position');
    }

    if (this.filled) {
      svg.setAttribute('data-filled', 'true');
    } else {
      svg.removeAttribute('data-filled');
    }

    if (this.color) {
      svg.setAttribute('data-color', this.color);
    } else {
      svg.removeAttribute('data-color');
    }
    return svg;
  }

  async getIcon() {
    try {
      const { [iconName(this.icon)]: svg } = await import('@fil-global/gds-streamline-icons/esm');

      this.svg = svg;
      this.svg.setAttribute('title', this.icon);
      // eslint-disable-next-line
    } catch (error) {
      // TODO suppress the error currently since it always throw error when invokes this component, will improve later with #93 in github issues.
      // console.error(err);
    }
  }

  render() {
    this.getIcon();

    if (this.svg) {
      const el = document.createElement('div');
      el.innerHTML = this.svg;
      const svgElement = this.svgModifier(el.getElementsByTagName('svg')[0]);
      const svg = svgElement ? this.svgModifier(svgElement) : '';
      return html`${svg}`;
    }

    return null;
  }
}

export default FilIcon;
